// Bridge between the web app and the Diffit Chrome extension. The
// extension declares this app in its manifest's `externally_connectable`,
// which lets us fire chrome.runtime.sendMessage from web pages directly
// to the extension's service worker.
//
// We use this to tell the extension when the user finishes signing in,
// so its side panel can reload and pick up the new session — Clerk's
// chrome-extension SDK does not propagate mid-session syncHost updates
// reliably, so an explicit notification is the most robust path.
// See: https://clerk.com/docs/guides/sessions/sync-host

// Two IDs, because a locally-loaded build and the published one differ — by
// design, not by accident.
//
// The first is the published Chrome Web Store item, which is what essentially
// every real user has installed. scripts/build-store.mjs in the extension repo
// runs `delete manifest.key` before zipping, so the uploaded package carries no
// key and the store applies its own identity.
//
// The second is what an unpacked local build resolves to, since it keeps the
// `key` from wxt.config.ts and Chrome hashes that into an ID.
//
// Both have to be listed or one of the two environments silently loses sign-in
// sync. Adding an ID is cheap — sendMessage to an ID nobody has installed is a
// no-op (see the lastError swallow below) — so err toward listing both.
const DIFFIT_EXTENSION_IDS = [
  'gohaeddhlhcjokibckjmphegiieiakjh', // published Web Store item
  'ecpafgeicidhdggjkkcddambekcokkpe', // unpacked local build (wxt.config.ts `key`)
];

export function notifyExtensionOfSignIn() {
  if (typeof window === 'undefined') return;
  const sendMessage = window.chrome?.runtime?.sendMessage;
  if (typeof sendMessage !== 'function') return;
  for (const id of DIFFIT_EXTENSION_IDS) {
    try {
      sendMessage(id, { type: 'AUTH_SIGNED_IN' }, () => {
        // Swallow chrome.runtime.lastError when the extension isn't
        // installed or the message couldn't be delivered — non-fatal.
        void window.chrome?.runtime?.lastError;
      });
    } catch {
      /* extension not installed; ignore */
    }
  }
}

// Chrome answers a message to an extension that is not installed (or is disabled) with
// "Receiving end does not exist". Any other outcome — a reply, or the extension closing the
// port because it has no handler for PING — means it is there. So this needs no handler in the
// extension. With no extension that lists this origin, the page gets no sendMessage at all.
const NOT_INSTALLED_ERROR = 'Receiving end does not exist';
// Caps how long a waking service worker can hold the slot blank. A missing extension answers at
// once, so no answer by then counts as installed.
const PING_TIMEOUT_MS = 1000;

const pingExtension = (sendMessage, id) =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(true), PING_TIMEOUT_MS);
    try {
      sendMessage(id, { type: 'PING' }, () => {
        clearTimeout(timer);
        const error = window.chrome?.runtime?.lastError;
        resolve(!error?.message?.includes(NOT_INSTALLED_ERROR));
      });
    } catch {
      clearTimeout(timer);
      resolve(false);
    }
  });

export async function isExtensionInstalled() {
  if (typeof window === 'undefined') return false;
  const sendMessage = window.chrome?.runtime?.sendMessage;
  if (typeof sendMessage !== 'function') return false;
  const results = await Promise.all(
    DIFFIT_EXTENSION_IDS.map((id) => pingExtension(sendMessage, id))
  );
  return results.some(Boolean);
}
